import { Button, Flex, Text } from '@chakra-ui/react'
import type { AcquisitionNumber, OrganizationUnit } from 'BrokerTsLib/src/dtos'
import type {
  Product
} from 'BrokerTsLib/src/enums'
import {
  FlexGroupType,
  getProductFullName,
  OrganizationUnitType
} from 'BrokerTsLib/src/enums'
import {
  useGetAcquisitionNumbers,
  useGetInsurers,
} from 'BrokerTsLib/src/hooks/useOrganizationUnit'
import React, { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'

import {
  AcquisitionNumbersTable,
  CustomWaypoint,
  PageSection,
} from '@/components'
import { TableSection } from '@/components/Section/TableSection'
import { useAuth } from '@/context/AuthContext'
import { usePartner } from '@/context/PartnerContext'
import { useSidebar } from '@/context/SidebarContext'
import { useAvailableProducts } from '@/hooks/useProducts'
import { Add } from '@/icons'
import type { PageSection as Section } from '@/layouts/components/Sidebar'

import { useOrganizationFlexGroups } from 'BrokerTsLib/src/hooks/useFlexGroup'
import { DependentOrganizationUnitList } from './DependentOrganizationUnitList'
import { OrganizationInfo } from './OrganizationInfo'
import { OrganizationInsurersTable } from './OrganizationInsurersTable'
import { OrganizationMotivation } from './OrganizationMotivation'
import { OrganizationUnitFlexGroups } from './OrganizationUnitFlexGroups'
import { OrganizationUnitSharedFlexGroups } from './OrganizationUnitSharedFlexGroups'
import { OrganizationUserList } from './OrganizationUserList'

export const OrganizationDetail = ({
  organization,
  isLoading,
}: {
  organization?: OrganizationUnit
  isLoading?: boolean
}) => {
  const { pageSections, setPageSections } = useSidebar()
  const { currentPartner } = usePartner()
  const products = useAvailableProducts()
  const { user } = useAuth()

  const { data: acquisitionNumbersData } = useGetAcquisitionNumbers(
    organization?.id,
    undefined,
    {
      enabled: !!organization,
    },
  )
  const acquisitionNumbers = useMemo(
    () => acquisitionNumbersData?.data ?? [],
    [acquisitionNumbersData],
  )
  
  const { flexGroups: defaultFlexGroups } = useOrganizationFlexGroups(organization?.id ?? 0, FlexGroupType.Default, !!organization?.id && !!user?.isSupervisor)
  const { flexGroups: sharedFlexGroups } = useOrganizationFlexGroups(organization?.id ?? 0, FlexGroupType.Shared, !!organization?.id && !!user?.isSupervisor)

  useEffect(() => {
    const array: Section[] = []
    let i = 1

    if (organization) {
      array.push({
        id: 'organizationInfo',
        sectionTitle: 'Informace o organizaci',
        index: i++,
        children: (
          <CustomWaypoint sectionId="organizationInfo">
            <PageSection id={'organizationInfo'}>
              <OrganizationInfo organization={organization} />
            </PageSection>
          </CustomWaypoint>
        ),
      })

      if (acquisitionNumbers && acquisitionNumbers.length > 0) {
        array.push({
          id: 'acquisitionNumbers',
          sectionTitle: 'Ziskatelská čísla',
          index: i++,
          children: (
            <CustomWaypoint sectionId="acquisitionNumbers">
              <PageSection id={'acquisitionNumbers'}>
                <AcquisitionNumbersSection
                  acquisitionNumbers={acquisitionNumbers}
                />
              </PageSection>
            </CustomWaypoint>
          ),
        })
      }

      if (
        currentPartner?.partner === 'Jap' &&
        organization.type === OrganizationUnitType.Partner
      ) {
        array.push({
          id: 'motivation',
          sectionTitle: 'Zařazení',
          index: i++,
          children: (
            <CustomWaypoint sectionId="motivation">
              <PageSection id={'motivation'}>
                <OrganizationMotivation organization={organization} />
              </PageSection>
            </CustomWaypoint>
          ),
        })
      }

      array.push({
        id: 'organizationUsers',
        sectionTitle: 'Uživatelé',
        index: i++,
        children: (
          <CustomWaypoint sectionId="organizationUsers">
            <PageSection id={'organizationUsers'}>
              <OrganizationUserList organization={organization} />
            </PageSection>
          </CustomWaypoint>
        ),
      })

      array.push({
        id: 'dependentOrganizations',
        sectionTitle: 'Podřízené organizace',
        index: i++,
        children: (
          <CustomWaypoint sectionId="dependentOrganizations">
            <PageSection id={'dependentOrganizations'}>
              <DependentOrganizationsSection organization={organization} />
            </PageSection>
          </CustomWaypoint>
        ),
      })

      if ((defaultFlexGroups?.length ?? 0) > 0 && user?.isSupervisor) {
        array.push({
          id: 'flexGroupsDefault',
          sectionTitle: 'Flexibilní skupiny',
          index: i++,
          children: (
            <CustomWaypoint sectionId="flexGroupsDefault">
              <PageSection id={'flexGroupsDefault'}>
                <OrganizationUnitFlexGroups
                  organizationUnitId={organization.id}
                />
              </PageSection>
            </CustomWaypoint>
          ),
        })
      }

      if ((sharedFlexGroups?.length ?? 0) > 0 && user?.isSupervisor){
        array.push({
          id: 'flexGroups',
          sectionTitle: 'Sdílené skupiny',
          index: i++,
          children: (
            <CustomWaypoint sectionId="flexGroups">
              <PageSection id={'flexGroups'}>
                <OrganizationUnitSharedFlexGroups organizationUnitId={organization.id} />
              </PageSection>
            </CustomWaypoint>
          )
        })
      }

      for (const product of products) {
        array.push(getInsurersSection(i++, product, organization))
      }
    }

    setPageSections(array)

    return () => setPageSections([])
  }, [user, organization, acquisitionNumbers, setPageSections, currentPartner?.partner, sharedFlexGroups, products])

  return pageSections.map((x) => (
    <React.Fragment key={x.id}>{x.children}</React.Fragment>
  ))
}

const AcquisitionNumbersSection = ({
  acquisitionNumbers,
}: {
  acquisitionNumbers: AcquisitionNumber[]
}) => {
  return (
    <TableSection sectionTitle={'Ziskatelská čísla'} wide>
      <AcquisitionNumbersTable rows={acquisitionNumbers} />
    </TableSection>
  )
}

const InsurersSection = ({
  organization,
  title,
  type,
}: {
  organization: OrganizationUnit
  title: string
  type: Product
}) => {
  const { data: insurersData, isLoading: isLoading } = useGetInsurers(
    organization.id,
    type,
  )

  return (
    <TableSection sectionTitle={title} wide>
      <OrganizationInsurersTable
        insurers={insurersData}
        isLoading={isLoading}
      />
    </TableSection>
  )
}

const getInsurersSection = (
  index: number,
  type: Product,
  organization: OrganizationUnit,
) => {
  const id = type
  const title = getProductFullName(type)

  if (!title) throw new Error('Invalid insurance category type')

  return {
    id,
    sectionTitle: title,
    index,
    children: (
      <CustomWaypoint sectionId={id}>
        <PageSection id={id}>
          <InsurersSection
            organization={organization}
            title={title}
            type={type}
          />
        </PageSection>
      </CustomWaypoint>
    ),
  }
}

const DependentOrganizationsSection = ({
  organization,
}: {
  organization: OrganizationUnit
}) => {
  const { user } = useAuth()

  return (
    <TableSection
      bodyProps={{ sx: { pb: 0 } }}
      sectionTitle={
        <Flex justifyContent={'space-between'}>
          <Text>Podřízené organizace</Text>
          {user?.allowEditOrganizationStructure &&
            <Button
              colorScheme="primary"
              size="xs"
              variant="link"
              as={Link}
              to={`/organization/${organization.id}/createDependent`}
            >
              Vytvořit podřízený subjekt
              <Add />
            </Button>}
        </Flex>
      }
      wide
    >
      <DependentOrganizationUnitList organization={organization} />
    </TableSection>
  )
}
